import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store';
import { useGroupsApi } from '../api';
import { resetError, resetGroup as resetG, setError, setGroup, setLoading } from '../slices';
import {
  CreateGroupRequestData,
  TestGroupRulesRequestData,
  UpdateGroupRequestData,
} from '../types';

export const useGroupService = () => {
  const dispatch = useDispatch();
  const { loading, group, error } = useAppSelector(state => state.groups);

  const api = useGroupsApi();

  const getGroup = useCallback(async (companyId: string, groupId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getById: true }));

      const response = await api.getGroup(companyId, groupId);
      dispatch(setGroup(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getById: false }));
    }
  }, []);

  const getDefaultGroup = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getById: true }));

      const response = await api.getDefaultGroup(companyId);
      dispatch(setGroup(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getById: false }));
    }
  }, []);

  const createGroup = useCallback(async (companyId: string, data: CreateGroupRequestData) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ create: true }));

      return await api.createGroup(companyId, data);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ create: false }));
    }
  }, []);

  const updateGroup = useCallback(
    async (companyId: string, groupId: string, data: UpdateGroupRequestData) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ update: true }));

        await api.updateGroup(companyId, groupId, data);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ update: false }));
      }
    },
    [],
  );

  const archiveGroup = useCallback(async (companyId: string, groupId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ archive: true }));

      await api.archiveUnarchiveGroup(companyId, groupId, 'archived');
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ archive: false }));
    }
  }, []);

  const unarchiveGroup = useCallback(async (companyId: string, groupId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ unarchive: true }));

      await api.archiveUnarchiveGroup(companyId, groupId, 'active');
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ unarchive: false }));
    }
  }, []);

  const addContactsToGroup = useCallback(
    async (companyId: string, groupId: string, contactIds: string[]) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ addContactsToGroup: true }));

        await api.addContactsToGroup(companyId, groupId, contactIds);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ addContactsToGroup: false }));
      }
    },
    [],
  );

  const removeContactsFromGroup = useCallback(
    async (companyId: string, groupId: string, contactIds: string[]) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ removeContactsFromGroup: true }));

        await api.removeContactsFromGroup(companyId, groupId, contactIds);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ removeContactsFromGroup: false }));
      }
    },
    [],
  );

  const testGroupRules = useCallback(async (companyId: string, data: TestGroupRulesRequestData) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ testGroupRules: true }));

      return await api.testGroupRules(companyId, data);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ testGroupRules: false }));
    }
  }, []);

  /** Active join and enrollment campaigns of the groups, each once. */
  const getJoinCampaigns = useCallback(
    async (companyId: string, groupIds: string[]) => {
      const response = await api.getJoinCampaigns(companyId, groupIds);
      return response.data;
    },
    [api],
  );

  /**
   * Active join and enrollment campaigns of the default group, the one every contact is in. Unlike
   * getDefaultGroup it does not store the group, so the group the page shows is left as it is.
   */
  const getDefaultGroupJoinCampaigns = useCallback(
    async (companyId: string) => {
      const response = await api.getDefaultGroup(companyId);
      return response.data?.joinCampaigns ?? [];
    },
    [api],
  );

  const resetGroup = useCallback(() => {
    dispatch(resetG());
  }, []);

  return {
    loading,
    group,
    error,
    getGroup,
    getJoinCampaigns,
    getDefaultGroupJoinCampaigns,
    getDefaultGroup,
    createGroup,
    updateGroup,
    archiveGroup,
    unarchiveGroup,
    addContactsToGroup,
    removeContactsFromGroup,
    testGroupRules,
    resetGroup,
  };
};
